import './bootIndicator.css'

/**
 * The full-page boot indicator — #262.
 *
 * The design system names exactly one situation for it: *"App shell boot,
 * sign-in and full-page waits. Borrows the background mesh — brand-first, one
 * per view, never inside a widget."* Those last three clauses are constraints,
 * not description:
 *
 *   - **One per view.** It is the loudest indicator in the system. A page
 *     showing this does not also skeleton the widgets inside it — which is
 *     free here, because the only caller renders it INSTEAD of the shell.
 *   - **Never inside a widget.** `BootIndicator.test.tsx` pins the importer
 *     set rather than trusting the comment; a second caller has to be argued.
 *   - **Borrows the background mesh.** Literally — the hexagon, the palette
 *     and the stagger are the Design System's own markup, transcribed in
 *     `bootIndicator.css`.
 *
 * **It ships no timing policy.** The 200ms floor and 8s ceiling belong to all
 * six indicators and are #254's to place in `fromQuery`; inventing a local
 * delay here would be a second implementation of a rule that does not exist
 * yet, and the wrong place for the first one. The practical cost is a brief
 * appearance on a fast boot, which is one flash per document load rather than
 * per navigation — `RequireAuth` reaches `unknown` only before the first
 * answer, not on every route change.
 *
 * The cluster is `aria-hidden` and the label carries the meaning. A live
 * region is deliberately NOT used: this mounts as part of the first paint, so
 * there is no change for one to observe, and `role="status"` on content that
 * arrives with the document announces unreliably across screen readers. The
 * visible text is what a user actually gets, in every theme and at every
 * motion preference.
 */
export function BootIndicator() {
  return (
    /**
     * `aria-busy` on the region whose content is loading — the Design System's
     * §Loading & progress line, and #264's subject for `<Async>`.
     *
     * It is answerable here in a way it is not there: this element wraps the
     * whole wait, exists in exactly one state, and disappears when the wait
     * ends. There is no clearing edge to get wrong because there is nothing
     * left to clear.
     */
    <div className="boot" aria-busy="true">
      {/* Decorative. Seven cells in three columns; the outer two are offset so
          the flat columns tessellate. Positional, not labelled: the fills and
          delays are addressed by :nth-child in bootIndicator.css, which keeps
          every value out of the component and out of JS — D2, and rule 1's
          "no JS theme object" in particular. */}
      <div className="boot-hive" aria-hidden="true">
        <div className="boot-hive__col boot-hive__col--offset">
          <span className="boot-hive__hex hex-pulse" />
          <span className="boot-hive__hex hex-pulse" />
        </div>
        <div className="boot-hive__col">
          <span className="boot-hive__hex hex-pulse" />
          <span className="boot-hive__hex hex-pulse" />
          <span className="boot-hive__hex hex-pulse" />
        </div>
        <div className="boot-hive__col boot-hive__col--offset">
          <span className="boot-hive__hex hex-pulse" />
          <span className="boot-hive__hex hex-pulse" />
        </div>
      </div>

      <p className="boot__label">Loading…</p>
    </div>
  )
}
